import { GshopVariantFieldType } from "@redotech/cockroachdb-codegen/tables/product/gshop-variant-field-type";
import { ListingType } from "@redotech/cockroachdb-codegen/tables/product/listing-type";
import { attributeDefinitionIdFormat } from "@redotech/redo-product-id";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { AttributeType, AttributeValueSchema } from "../attributes";
import { BaseProductListingSchema } from "../base-listing";
import {
  GoogleShoppingAgeGroupSchema,
  GoogleShoppingAvailabilitySchema,
  GoogleShoppingConditionSchema,
  GoogleShoppingGenderSchema,
  GoogleShoppingPriceSchema,
  GoogleShoppingShippingDimensionSchema,
  GoogleShoppingShippingWeightSchema,
} from "./common-schemas";
import {
  GoogleShoppingListingMediaSchema,
  GoogleShoppingListingMediaWithGoogleIds,
  GoogleShoppingListingMediaWithoutRedoIds,
} from "./google-shopping-media";

export const GoogleShoppingVariantFieldTypeSchema = createEnum(
  GshopVariantFieldType,
);
export type GoogleShoppingVariantFieldType = z.infer<
  typeof GoogleShoppingVariantFieldTypeSchema
>;

export const GoogleShoppingVariantListingSchema_ =
  BaseProductListingSchema.extend({
    listingType: z.literal(ListingType.GoogleShopping),
    id: z.bigint(),
    offerId: z.string(),
    price: GoogleShoppingPriceSchema,
    salePrice: GoogleShoppingPriceSchema.nullable(),
    availability: GoogleShoppingAvailabilitySchema,
    condition: GoogleShoppingConditionSchema.nullable(),
    gtin: z.string().nullable(),
    mpn: z.string().nullable(),
    shippingWeight: GoogleShoppingShippingWeightSchema.nullable(),
    shippingLength: GoogleShoppingShippingDimensionSchema.nullable(),
    shippingWidth: GoogleShoppingShippingDimensionSchema.nullable(),
    shippingHeight: GoogleShoppingShippingDimensionSchema.nullable(),
    color: z.string().nullable(),
    size: z.string().nullable(),
    material: z.string().nullable(),
    pattern: z.string().nullable(),
    ageGroup: GoogleShoppingAgeGroupSchema.nullable(),
    gender: GoogleShoppingGenderSchema.nullable(),
    media: z.array(GoogleShoppingListingMediaSchema),
    createdAt: z.date(),
    updatedAt: z.date(),
  });
export const GoogleShoppingVariantListingSchema: z.ZodType<GoogleShoppingVariantListing> =
  GoogleShoppingVariantListingSchema_;
export interface GoogleShoppingVariantListing extends z.infer<
  typeof GoogleShoppingVariantListingSchema_
> {}

export type GoogleShoppingVariantListingWithoutRedoIds = Omit<
  GoogleShoppingVariantListing,
  "id" | "productId" | "media"
> & { media: GoogleShoppingListingMediaWithoutRedoIds[] };

export type GoogleShoppingVariantListingWithGoogleIds = Omit<
  GoogleShoppingVariantListing,
  "media"
> & { media: GoogleShoppingListingMediaWithGoogleIds[] };

const GoogleShoppingVariantMappingSchema_ = z.object({
  fieldName: GoogleShoppingVariantFieldTypeSchema,
  attributeDefinitionId: attributeDefinitionIdFormat.schema,
});
export const GoogleShoppingVariantMappingSchema: z.ZodType<GoogleShoppingVariantMapping> =
  GoogleShoppingVariantMappingSchema_;
export interface GoogleShoppingVariantMapping extends z.infer<
  typeof GoogleShoppingVariantMappingSchema_
> {}

export const GoogleShoppingVariantAttributeType = {
  [GshopVariantFieldType.OfferId]: AttributeType.ShortText,
  [GshopVariantFieldType.Price]: AttributeType.Money,
  [GshopVariantFieldType.SalePrice]: AttributeType.Money,
  [GshopVariantFieldType.Availability]: AttributeType.Enum,
  [GshopVariantFieldType.Condition]: AttributeType.Enum,
  [GshopVariantFieldType.Gtin]: AttributeType.ShortText,
  [GshopVariantFieldType.Mpn]: AttributeType.ShortText,
  [GshopVariantFieldType.ShippingWeight]: AttributeType.Weight,
  [GshopVariantFieldType.ShippingLength]: AttributeType.Float,
  [GshopVariantFieldType.ShippingWidth]: AttributeType.Float,
  [GshopVariantFieldType.ShippingHeight]: AttributeType.Float,
  [GshopVariantFieldType.Color]: AttributeType.ShortText,
  [GshopVariantFieldType.Size]: AttributeType.ShortText,
  [GshopVariantFieldType.Material]: AttributeType.ShortText,
  [GshopVariantFieldType.Pattern]: AttributeType.ShortText,
  [GshopVariantFieldType.AgeGroup]: AttributeType.Enum,
  [GshopVariantFieldType.Gender]: AttributeType.Enum,
} satisfies Record<GshopVariantFieldType, AttributeType>;

export const GoogleShoppingVariantAttributeSchema = {
  [GshopVariantFieldType.OfferId]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.OfferId]
    ].nullable(),
  [GshopVariantFieldType.Price]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.Price]
    ].nullable(),
  [GshopVariantFieldType.SalePrice]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.SalePrice]
    ].nullable(),
  [GshopVariantFieldType.Availability]:
    GoogleShoppingAvailabilitySchema.nullable(),
  [GshopVariantFieldType.Condition]: GoogleShoppingConditionSchema.nullable(),
  [GshopVariantFieldType.Gtin]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.Gtin]
    ].nullable(),
  [GshopVariantFieldType.Mpn]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.Mpn]
    ].nullable(),
  [GshopVariantFieldType.ShippingWeight]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.ShippingWeight]
    ].nullable(),
  [GshopVariantFieldType.ShippingLength]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.ShippingLength]
    ].nullable(),
  [GshopVariantFieldType.ShippingWidth]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.ShippingWidth]
    ].nullable(),
  [GshopVariantFieldType.ShippingHeight]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.ShippingHeight]
    ].nullable(),
  [GshopVariantFieldType.Color]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.Color]
    ].nullable(),
  [GshopVariantFieldType.Size]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.Size]
    ].nullable(),
  [GshopVariantFieldType.Material]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.Material]
    ].nullable(),
  [GshopVariantFieldType.Pattern]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.Pattern]
    ].nullable(),
  [GshopVariantFieldType.AgeGroup]: GoogleShoppingAgeGroupSchema.nullable(),
  [GshopVariantFieldType.Gender]: GoogleShoppingGenderSchema.nullable(),
};

export type GoogleShoppingVariantAttributeValueType = {
  [key in GshopVariantFieldType]: (typeof GoogleShoppingVariantAttributeSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export function getGoogleShoppingVariantAttributeValue<
  T extends GshopVariantFieldType,
>(field: T, value: unknown): GoogleShoppingVariantAttributeValueType[T] | null {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return GoogleShoppingVariantAttributeSchema[field].parse(
    value,
  ) as GoogleShoppingVariantAttributeValueType[T];
}

export function getGoogleShoppingVariantAttributeType<
  T extends GshopVariantFieldType,
>(field: T): (typeof GoogleShoppingVariantAttributeType)[T] {
  return GoogleShoppingVariantAttributeType[field];
}
